import { useNavigate, useLocation } from 'react-router-dom'
import { Bot, BarChart3, TrendingUp, User, Eye } from 'lucide-react'
import { motion } from 'framer-motion'
import { useBottomNav } from '@/shared/contexts/BottomNavContext'

const navItems = [
  { path: '/', icon: Bot, label: 'Боты' },
  { path: '/traffic', icon: TrendingUp, label: 'Трафик' },
  { path: '/shows', icon: Eye, label: 'Показы' },
  { path: '/statistics', icon: BarChart3, label: 'Статистика' },
  { path: '/profile', icon: User, label: 'Профиль' },
]

interface BottomNavProps {
  isVisible?: boolean
}

export default function BottomNav({ isVisible = true }: BottomNavProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { isBottomNavHidden } = useBottomNav()

  const handleNavClick = (path: string) => {
    try {
      window.Telegram?.WebApp?.HapticFeedback?.selectionChanged()
    } catch (e) {
      // Haptic not supported, silently ignore
    }
    navigate(path)
  }

  if (!isVisible || isBottomNavHidden) return null

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-tg-bg">
      <div className="absolute inset-0 bg-tg-bg/95 backdrop-blur-xl" />

      <div className="relative flex justify-around items-center h-20 px-1 pt-2 pb-2 mb-[30px] bg-tg-bg">
        {navItems.map(({ path, icon: Icon, label }) => {
          const isActive = location.pathname === path

          return (
            <button
              key={path}
              onClick={() => handleNavClick(path)}
              className="relative flex flex-col items-center justify-center flex-1 h-full group min-w-0"
            >
              {isActive && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute inset-x-1 inset-y-2 bg-tg-button/10 rounded-2xl"
                  initial={false}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                  }}
                />
              )}

              <motion.div
                className="relative z-10 mb-1"
                animate={{
                  scale: isActive ? 1 : 0.9,
                  y: isActive ? -2 : 0,
                }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-tg-button/20 rounded-full blur-lg"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  />
                )}

                <div
                  className={`relative p-2 rounded-xl transition-all duration-200 ${
                    isActive ? 'bg-tg-button shadow-lg shadow-tg-button/25' : 'group-active:scale-95'
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 transition-colors ${
                      isActive ? 'text-tg-button-text' : 'text-tg-hint group-hover:text-tg-text'
                    }`}
                    strokeWidth={isActive ? 2.5 : 2}
                  />
                </div>
              </motion.div>

              <motion.span
                className={`relative z-10 text-[11px] font-medium transition-all duration-200 truncate max-w-full px-0.5 ${
                  isActive ? 'text-tg-button font-semibold' : 'text-tg-hint'
                }`}
                animate={{
                  scale: isActive ? 1 : 0.9,
                  opacity: isActive ? 1 : 0.7,
                }}
              >
                {label}
              </motion.span>

              {isActive && (
                <motion.div
                  className="absolute bottom-[-5px] w-1 h-1 bg-tg-button rounded-full"
                  layoutId="activeDot"
                  initial={false}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                  }}
                />
              )}
            </button>
          )
        })}
      </div>
    </nav>
  )
}
